import React, { useEffect, useRef, useState } from "react";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import BottomSheet from "./BottomSheet";
import { feedbackApi } from "../services/feedback";
import "./ShareFeedbackSheet.css";
import { t, type MessageKey } from "../i18n";

// Share Feedback (founder, build 44 — look A): a moving picture, "Help shape
// Request App", what kind of note it is (an idea, some feedback or a bug),
// one text box, Send, and the Feedback board one tap away. It opens first;
// ideas and feedback land on the board after Send. Bugs come straight to us
// and never go on the board (the server enforces that too). The board needs a
// title, so the note's first line becomes its subject.

const FEEDBACK_MSG_MAX = 1000;
const FEEDBACK_SUBJECT_MAX = 120;
const FEEDBACK_PHOTOS_MAX = 3;
const TITLE_CHARS = 80;

type Kind = "idea" | "feedback" | "bug";
const KINDS: Kind[] = ["idea", "feedback", "bug"];
const KIND_LABEL: Record<Kind, MessageKey> = {
  idea: "shareFeedbackSheet.idea",
  feedback: "shareFeedbackSheet.feedback",
  bug: "shareFeedbackSheet.bug",
};
const PLACEHOLDER: Record<Kind, MessageKey> = {
  idea: "shareFeedbackSheet.placeholderIdea",
  feedback: "shareFeedbackSheet.placeholderFeedback",
  bug: "shareFeedbackSheet.placeholderBug",
};

/** The board's title for a note: its first line, cut at a word near 80 characters. */
export function subjectFrom(message: string): string {
  const line = message.trim().split(/\r?\n/)[0].trim();
  if (line.length <= TITLE_CHARS) return line.slice(0, FEEDBACK_SUBJECT_MAX);
  const cut = line.slice(0, TITLE_CHARS);
  const space = cut.lastIndexOf(" ");
  return `${(space > TITLE_CHARS / 2 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

// Silent triage context — which build is talking. Rides every feedback send
// so a bug report always says what version/device it came from without the
// talent typing a word. App.getInfo() is native-only (the call rejects on
// web); platform alone still tells us "web". NOTE: call the plugin METHOD and
// await its promise — never await the plugin object itself (Capacitor
// proxies fake a .then and hang).
async function collectAppContext(): Promise<{ appVersion: string | null; appBuild: string | null; platform: string }> {
  let appVersion: string | null = null;
  let appBuild: string | null = null;
  try {
    const info = await App.getInfo();
    appVersion = info?.version || null;
    appBuild = info?.build || null;
  } catch {
    /* web — no native shell */
  }
  return { appVersion, appBuild, platform: Capacitor.getPlatform() };
}

const svg = { width: 15, height: 15, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true } as const;
const KIND_ICON: Record<Kind, React.ReactNode> = {
  idea: (
    <svg {...svg}><path d="M9 18h6M10 21h4" /><path d="M12 3a6 6 0 0 0-3.6 10.8c.7.6 1.1 1.3 1.1 2.2h5c0-.9.4-1.6 1.1-2.2A6 6 0 0 0 12 3Z" /></svg>
  ),
  feedback: (
    <svg {...svg}><path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.9 8.9 0 0 1-3.9-.9L3 20l1-4.1a8.4 8.4 0 0 1-1-4.4 8.5 8.5 0 0 1 9-8.4 8.5 8.5 0 0 1 9 8.4z" /></svg>
  ),
  bug: (
    <svg {...svg}><rect x="7" y="8" width="10" height="12" rx="5" /><path d="M9 8a3 3 0 0 1 6 0M12 12v6M3 13h4M17 13h4M4 7l3 2M20 7l-3 2M4 19l3-2M20 19l-3-2" /></svg>
  ),
};
const CAMERA = (
  <svg {...svg} width={16} height={16}><path d="M4 8h3l2-3h6l2 3h3v11H4z" /><circle cx="12" cy="13" r="3.5" /></svg>
);
const BOARD = (
  <svg {...svg} width={16} height={16}><path d="M12 5l6 7H6z" fill="currentColor" stroke="none" /><path d="M5 17h14M8 21h8" /></svg>
);
const CLOSE = (
  <svg viewBox="0 0 12 12" width="11" height="11" aria-hidden="true">
    <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
  </svg>
);
const sparkle = (cls: string, x: number, y: number, r: number, fill: string) => (
  <path className={`sfb-tw ${cls}`} d={`M${x} ${y - r}l${r * 0.38} ${r * 0.62} ${r * 0.62} ${r * 0.38}-${r * 0.62} ${r * 0.38}-${r * 0.38} ${r * 0.62}-${r * 0.38}-${r * 0.62}-${r * 0.62}-${r * 0.38} ${r * 0.62}-${r * 0.38}Z`} fill={fill} />
);

// The picture (founder pick A): a green phone with a megaphone, a pink chat
// bubble, twinkling sparkles. It floats; prefers-reduced-motion keeps it still.
const FeedbackHero: React.FC = () => (
  <svg className="sfb-hero" viewBox="0 0 240 132" aria-hidden="true">
    <defs>
      <linearGradient id="sfb-phone" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#4ade80" /><stop offset="1" stopColor="#15803d" /></linearGradient>
      <linearGradient id="sfb-bubble" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#f472b6" /><stop offset="1" stopColor="#a855f7" /></linearGradient>
      <radialGradient id="sfb-glow"><stop offset="0" stopColor="rgba(36,201,97,0.35)" /><stop offset="1" stopColor="rgba(36,201,97,0)" /></radialGradient>
    </defs>
    <circle cx="122" cy="66" r="62" fill="url(#sfb-glow)" />
    <g className="sfb-bob">
      <g transform="rotate(14 124 64)">
        <rect x="98" y="12" width="54" height="98" rx="13" fill="url(#sfb-phone)" />
        <rect x="109" y="21" width="24" height="4.5" rx="2.2" fill="rgba(255,255,255,0.55)" />
        <g transform="translate(108 48)" fill="#fff">
          <path d="M4 10h7l15-9v28l-15-9H4a3 3 0 0 1-3-3v-4a3 3 0 0 1 3-3Z" />
          <path d="M7 21h5l2 9a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-1.6Z" />
        </g>
      </g>
    </g>
    <g className="sfb-pop">
      <path d="M58 76h40a11 11 0 0 1 11 11v6a11 11 0 0 1-11 11H76l-9 8v-8h-9a11 11 0 0 1-11-11v-6a11 11 0 0 1 11-11Z" fill="url(#sfb-bubble)" />
      <path d="M60 91c3.5-6 7 6 10.5 0s7 6 10.5 0 7 6 10.5 0" stroke="#fff" strokeWidth="3" fill="none" strokeLinecap="round" />
    </g>
    {sparkle("sfb-tw--1", 178, 22, 8, "#a78bfa")}
    {sparkle("sfb-tw--2", 70, 31, 5, "#fde047")}
    {sparkle("sfb-tw--3", 190, 98, 4, "#4ade80")}
    <path d="M84 52l-7-5M81 63h-9" stroke="#4ade80" strokeWidth="3.2" strokeLinecap="round" />
  </svg>
);

const SentHero: React.FC = () => (
  <svg className="sfb-hero" viewBox="0 0 240 132" aria-hidden="true">
    <defs>
      <radialGradient id="sfb-glow-sent"><stop offset="0" stopColor="rgba(36,201,97,0.45)" /><stop offset="1" stopColor="rgba(36,201,97,0)" /></radialGradient>
    </defs>
    <circle cx="120" cy="66" r="62" fill="url(#sfb-glow-sent)" />
    <g className="sfb-pop">
      <circle cx="120" cy="66" r="38" fill="#24C961" />
      <path d="M103 66l12 12 22-24" stroke="#04120a" strokeWidth="7" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    {sparkle("sfb-tw--1", 176, 26, 8, "#a78bfa")}
    {sparkle("sfb-tw--2", 62, 37, 5, "#fde047")}
    {sparkle("sfb-tw--3", 180, 104, 4, "#4ade80")}
  </svg>
);

const ShareFeedbackSheet: React.FC<{ open: boolean; onClose: () => void; onSeeBoard: () => void }> = ({ open, onClose, onSeeBoard }) => {
  const [kind, setKind] = useState<Kind>("idea");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // What was just sent — the thank-you differs for a bug (private).
  const [sent, setSent] = useState<Kind | null>(null);

  // Up to 3 screenshots. Each picked photo relays through
  // POST /feedback/photo immediately (→ URL); the send itself just carries
  // the URLs. A 404 from the relay means photo storage isn't configured on
  // this server — hide the photo control rather than show a dead one.
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [photosUnavailable, setPhotosUnavailable] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);

  // Every open starts on the form (a dismiss mid-send could leave the thank-you
  // set); an unsent draft survives a stray backdrop tap.
  useEffect(() => {
    if (open) {
      setSent(null);
      setError(null);
    }
  }, [open]);

  const pickPhoto = () => {
    if (!uploading && photos.length < FEEDBACK_PHOTOS_MAX) fileInput.current?.click();
  };

  const onPhotoPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // same file must be re-pickable after a remove
    if (!file || photos.length >= FEEDBACK_PHOTOS_MAX) return;
    try {
      setUploading(true);
      setError(null);
      const fd = new FormData();
      fd.append("photo", file);
      const res = await feedbackApi.uploadPhoto(fd);
      const url = res?.url;
      if (url) setPhotos((prev) => [...prev, url].slice(0, FEEDBACK_PHOTOS_MAX));
    } catch (err: any) {
      if (err?.response?.status === 404) setPhotosUnavailable(true);
      else setError(err?.response?.data?.msg || t("morePage.couldnTAddThat"));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    const text = message.trim();
    if (!text) return;
    try {
      setBusy(true);
      setError(null);
      const context = await collectAppContext();
      // Ideas and feedback go on the board (the server's word filter can hold
      // one back); a bug is private.
      await feedbackApi.send({ subject: subjectFrom(text), message: text, photos, kind, board: kind !== "bug", ...context });
      setSent(kind);
      setMessage("");
      setPhotos([]);
    } catch (e) {
      console.error("Feedback submit failed", e);
      setError(t("morePage.couldnTSendJust"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <BottomSheet open={open} title={t("morePage.shareFeedback2")} onClose={onClose} plain>
      <div className="sfb">
        <button type="button" className="sfb-x" onClick={onClose} aria-label={t("bottomSheet.close")}>
          {CLOSE}
        </button>

        {sent ? (
          <>
            <SentHero />
            <h2 className="sfb-title">{sent === "bug" ? t("shareFeedbackSheet.sentBugTitle") : t("shareFeedbackSheet.sentTitle")}</h2>
            <p className="sfb-sub">{sent === "bug" ? t("shareFeedbackSheet.sentBugSub") : t("shareFeedbackSheet.sentSub")}</p>
            {sent !== "bug" && (
              <button type="button" className="sfb-send" onClick={onSeeBoard}>
                {t("shareFeedbackSheet.seeItOnBoard")}
              </button>
            )}
            <button type="button" className="sfb-ghost" onClick={onClose}>
              {t("morePage.done")}
            </button>
          </>
        ) : (
          <>
            <FeedbackHero />
            <h2 className="sfb-title">{t("shareFeedbackSheet.title")}</h2>
            <p className="sfb-sub">
              {t("shareFeedbackSheet.subtitle")}
              <br />
              {t("shareFeedbackSheet.weReadEveryNote")}
            </p>

            <div className="sfb-kinds" role="group" aria-label={t("shareFeedbackSheet.kindLabel")}>
              {KINDS.map((k) => (
                <button key={k} type="button" className={`sfb-kind${k === kind ? " sfb-kind--on" : ""}`} aria-pressed={k === kind} onClick={() => setKind(k)}>
                  {KIND_ICON[k]}
                  {t(KIND_LABEL[k])}
                </button>
              ))}
            </div>

            <textarea
              className="sfb-box"
              aria-label={t("shareFeedbackSheet.title")}
              placeholder={t(PLACEHOLDER[kind])}
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, FEEDBACK_MSG_MAX))}
              maxLength={FEEDBACK_MSG_MAX}
              rows={5}
            />
            <div className="sfb-row">
              {!photosUnavailable && photos.length < FEEDBACK_PHOTOS_MAX ? (
                <button type="button" className="sfb-photo" onClick={pickPhoto} disabled={uploading}>
                  {uploading ? (
                    <span className="sfb-dots" role="status" aria-label={t("morePage.uploadingPhoto")}><i /><i /><i /></span>
                  ) : (
                    <>
                      {CAMERA}
                      {t("morePage.addAPhoto")}
                    </>
                  )}
                </button>
              ) : (
                <span />
              )}
              <span className="sfb-count">{message.length}/{FEEDBACK_MSG_MAX}</span>
            </div>
            {photos.length > 0 && (
              <div className="sfb-shots">
                {photos.map((url) => (
                  <div key={url} className="sfb-shot">
                    <img src={url} alt="" />
                    <button type="button" className="sfb-shot-x" aria-label={t("morePage.removePhoto")} onClick={() => setPhotos((prev) => prev.filter((u) => u !== url))}>
                      {CLOSE}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }} onChange={onPhotoPicked} />

            {error && <div className="sfb-error">{error}</div>}
            {/* Say where it goes, so nobody posts something private by mistake. */}
            <p className="sfb-note">{kind === "bug" ? t("shareFeedbackSheet.bugNote") : t("shareFeedbackSheet.boardNote")}</p>

            <button
              type="button"
              className="sfb-send"
              // Also parked while a photo is mid-upload: sending in that window
              // would silently drop the photo from the report.
              disabled={busy || uploading || !message.trim()}
              onClick={submit}
            >
              {busy ? t("morePage.sending") : t("shareFeedbackSheet.send")}
            </button>
            <button type="button" className="sfb-ghost" onClick={onSeeBoard}>
              {BOARD}
              {t("shareFeedbackSheet.seeBoard")}
            </button>
          </>
        )}
      </div>
    </BottomSheet>
  );
};

export default ShareFeedbackSheet;
